import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Save, AlertCircle, Loader2 } from "lucide-react";
import hotelService, { Hotel } from "../../services/hotelService";

const HotelEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement>(null);
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState<string | null>(null);
  const defaultPaymentMethods = [
    "Cash",
    "Card",
    "Credit Card",
    "Debit Card",
    "UPI",
    "Net Banking",
    "Wallet",
    "Bank Transfer",
    "Cheque",
  ];
  const [acceptedPaymentMethods, setAcceptedPaymentMethods] = useState<
    string[]
  >([]);
  const [paymentMethodInput, setPaymentMethodInput] = useState("");

  const [formData, setFormData] = useState({
    hotel_name: "",
    hotel_type: "Hotel" as
      | "Hotel"
      | "Apartment"
      | "Resort"
      | "Hostel"
      | "Guesthouse"
      | "Villa",
    vervotech_id: "",
    star_rating: "",
    street_address: "",
    landmark: "",
    area: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    destination: "",
    reservation_contact_number: "",
    phone: "",
    reservation_email: "",
    support_email: "",
    hotel_website: "",
    checkin_time: "",
    checkout_time: "",
    hotel_description: "",
    terms_conditions: "",
    latitude: null as string | null,
    longitude: null as string | null,
    timezone: "",
    status: "active" as "active" | "inactive" | "suspended",
    currency: "INR",
    contact_person_name: "",
    designation: "",
    contact_email: "",
    contact_phone: "",
    tax_details: "",
    bank_account_details: "",
    markup_percentage: "",
    service_charge_percentage: "",
    cancellation_policy: "",
    cancellation_policy_text: "",
    cancellation_days: "3",
    cancellation_percentage: "25",
    no_show_policy: "",
    no_show_policy_text: "",
    no_show_percentage: "100",
    child_policy: "",
    child_rate: "",
    extra_adult_rate: "",
    extra_bed_rate: "",
    min_stay: "",
    max_stay: "",
    max_advance_booking_days: "",
    special_offer_name: "",
    special_offer_type: "Percentage Discount",
    special_offer_details: "",
    offer_valid_from: "",
    offer_valid_to: "",
    applicable_market: "India",
    remarks: "",
    special_conditions: "",
  });

  useEffect(() => {
    if (id) loadHotel();
  }, [id]);

  const loadHotel = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hotelService.getHotelById(parseInt(id!));
      const hotelData = response.data;
      setHotel(hotelData);

      // Helper function to format time from HH:MM:SS to HH:MM
      const formatTimeForInput = (time: string) => {
        if (!time) return "";
        // If time is in HH:MM:SS format, extract HH:MM
        if (time.includes(":")) {
          const parts = time.split(":");
          return `${parts[0]}:${parts[1]}`;
        }
        return time;
      };

      setFormData({
        hotel_name: hotelData.hotel_name || "",
        hotel_type: hotelData.hotel_type || "Hotel",
        vervotech_id: hotelData.vervotech_id || "",
        star_rating: hotelData.star_rating?.toString() || "",
        street_address: hotelData.street_address || "",
        landmark: hotelData.landmark || "",
        area: hotelData.area || "",
        city: hotelData.city || "",
        state: hotelData.state || "",
        zip_code: hotelData.zip_code || "",
        country: hotelData.country || "",
        destination: hotelData.destination || "",
        reservation_contact_number: hotelData.reservation_contact_number || "",
        phone: hotelData.phone || "",
        reservation_email: hotelData.reservation_email || "",
        support_email: hotelData.support_email || "",
        hotel_website: hotelData.hotel_website || "",
        checkin_time: formatTimeForInput(hotelData.checkin_time || ""),
        checkout_time: formatTimeForInput(hotelData.checkout_time || ""),
        hotel_description: hotelData.hotel_description || "",
        terms_conditions: hotelData.terms_conditions || "",
        latitude: hotelData.latitude?.toString() || null,
        longitude: hotelData.longitude?.toString() || null,
        timezone: hotelData.timezone || "",
        status: hotelData.status || "active",
        currency: hotelData.currency || "INR",
        contact_person_name: hotelData.contact_person_name || "",
        designation: hotelData.designation || "",
        contact_email: hotelData.contact_email || "",
        contact_phone: hotelData.contact_phone || "",
        tax_details: hotelData.tax_details || "",
        bank_account_details: hotelData.bank_account_details || "",
        markup_percentage: hotelData.markup_percentage?.toString() || "",
        service_charge_percentage:
          hotelData.service_charge_percentage?.toString() || "",
        cancellation_policy: hotelData.cancellation_policy || "",
        cancellation_policy_text:
          hotelData.cancellation_policy_text ||
          hotelData.cancellation_policy ||
          "",
        cancellation_days: hotelData.cancellation_days?.toString() || "1",
        cancellation_percentage:
          hotelData.cancellation_percentage?.toString() || "100",
        no_show_policy: hotelData.no_show_policy || "",
        no_show_policy_text:
          hotelData.no_show_policy_text || hotelData.no_show_policy || "",
        no_show_percentage: hotelData.no_show_percentage?.toString() || "100",
        child_policy: hotelData.child_policy || "",
        child_rate: hotelData.child_rate?.toString() || "",
        extra_adult_rate: hotelData.extra_adult_rate?.toString() || "",
        extra_bed_rate: hotelData.extra_bed_rate?.toString() || "",
        min_stay: hotelData.min_stay?.toString() || "",
        max_stay: hotelData.max_stay?.toString() || "",
        max_advance_booking_days:
          hotelData.max_advance_booking_days?.toString() || "",
        special_offer_name: hotelData.special_offer_name || "",
        special_offer_type:
          hotelData.special_offer_type || "Percentage Discount",
        special_offer_details: hotelData.special_offer_details || "",
        offer_valid_from: hotelData.offer_valid_from || "",
        offer_valid_to: hotelData.offer_valid_to || "",
        applicable_market: hotelData.applicable_market || "India",
        remarks: hotelData.remarks || "",
        special_conditions: hotelData.special_conditions || "",
      });
      setAcceptedPaymentMethods(
        hotelData.accepted_payment_methods
          ? hotelData.accepted_payment_methods
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item.length > 0)
          : []
      );
    } catch (err) {
      setError("Failed to load hotel details.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const addPaymentMethod = (method: string) => {
    const trimmed = method.trim();
    if (!trimmed) return;
    setAcceptedPaymentMethods((prev) => {
      if (prev.includes(trimmed)) return prev;
      return [...prev, trimmed];
    });
    setPaymentMethodInput("");
  };

  const removePaymentMethod = (method: string) => {
    setAcceptedPaymentMethods((prev) => prev.filter((item) => item !== method));
  };

  const handlePaymentKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      if (paymentMethodInput) {
        addPaymentMethod(paymentMethodInput);
      }
    }
  };

  const fetchTimezone = async (city: string, country: string) => {
    if (!city || !country) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/timezone/${encodeURIComponent(
          city
        )}/${encodeURIComponent(country)}`
      );
      const data = await response.json();
      if (data.timezone) {
        setFormData((prev) => ({ ...prev, timezone: data.timezone }));
      }
    } catch (error) {
      console.error("Error fetching timezone:", error);
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, city: value }));
    if (value && formData.country) {
      fetchTimezone(value, formData.country);
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, country: value }));
    if (value && formData.city) {
      fetchTimezone(formData.city, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = {
        ...formData,
        landmark: formData.landmark?.trim() || "",
        reservation_contact_number: formData.reservation_contact_number
          ? formData.reservation_contact_number.trim()
          : null,
        contact_person_name: formData.contact_person_name.trim(),
        designation: formData.designation.trim(),
        contact_email: formData.contact_email.trim(),
        contact_phone: formData.contact_phone.trim(),
        tax_details: formData.tax_details.trim(),
        bank_account_details: formData.bank_account_details.trim(),
        star_rating: formData.star_rating
          ? parseInt(formData.star_rating)
          : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        markup_percentage: formData.markup_percentage
          ? Math.round(parseFloat(formData.markup_percentage) * 100) / 100
          : null,
        service_charge_percentage: formData.service_charge_percentage
          ? Math.round(parseFloat(formData.service_charge_percentage) * 100) /
            100
          : null,
        min_stay: formData.min_stay ? parseInt(formData.min_stay) : null,
        max_stay: formData.max_stay ? parseInt(formData.max_stay) : null,
        checkin_time: formData.checkin_time || null,
        checkout_time: formData.checkout_time || null,
        cancellation_days: formData.cancellation_days
          ? parseInt(formData.cancellation_days)
          : 3,
        cancellation_percentage: formData.cancellation_percentage
          ? Math.round(parseFloat(formData.cancellation_percentage) * 100) / 100
          : 25,
        no_show_percentage: formData.no_show_percentage
          ? Math.round(parseFloat(formData.no_show_percentage) * 100) / 100
          : 100,
        accepted_payment_methods: acceptedPaymentMethods,
      };

      await hotelService.updateHotel(parseInt(id!), updateData);
      setSuccess("Hotel updated successfully!");
      setTimeout(() => navigate(`/hotels/${id}`), 1500);
    } catch (err: any) {
      if (
        err.response?.data?.errors &&
        Array.isArray(err.response.data.errors)
      ) {
        const fieldErrorsMap: { [key: string]: string } = {};
        const generalErrors: string[] = [];

        err.response.data.errors.forEach((error: any) => {
          if (error.path) {
            fieldErrorsMap[error.path] = error.msg;
          } else {
            generalErrors.push(error.msg);
          }
        });

        setFieldErrors(fieldErrorsMap);
        if (generalErrors.length > 0) {
          setError(`Validation failed: ${generalErrors.join(", ")}`);
        } else {
          setError("Please fix the validation errors below.");
        }
      } else {
        setError(err.response?.data?.message || "Failed to update hotel.");
        setFieldErrors({});
      }

      // Scroll to top to show error message after state updates
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading hotel details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate(`/hotels/${id}`)}
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">Edit Hotel</h1>
            <p className="text-gray-400">
              {hotel?.hotel_name}
              {hotel?.extranet_hotel_id && (
                <span className="ml-2">({hotel.extranet_hotel_id})</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-green-900/20 border border-green-800 rounded-md">
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {error && (
        <div
          ref={errorRef}
          className="flex items-center space-x-2 p-4 bg-red-900/20 border border-red-800 rounded-md"
        >
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hotel_name" className="text-gray-300">
                      Hotel Name *
                    </Label>
                    <Input
                      id="hotel_name"
                      name="hotel_name"
                      value={formData.hotel_name}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vervotech_id" className="text-gray-300">
                      Vervotech ID *
                    </Label>
                    <Input
                      id="vervotech_id"
                      name="vervotech_id"
                      value={formData.vervotech_id}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Enter Vervotech ID"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hotel_type" className="text-gray-300">
                      Hotel Type
                    </Label>
                    <select
                      id="hotel_type"
                      name="hotel_type"
                      value={formData.hotel_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md"
                    >
                      <option value="Hotel">Hotel</option>
                      <option value="Resort">Resort</option>
                      <option value="Apartment">Apartment</option>
                      <option value="Guesthouse">Guesthouse</option>
                      <option value="Villa">Villa</option>
                      <option value="Hostel">Hostel</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="star_rating" className="text-gray-300">
                      Star Rating
                    </Label>
                    <select
                      id="star_rating"
                      name="star_rating"
                      value={formData.star_rating}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md"
                    >
                      <option value="">Select Rating</option>
                      <option value="1">1 Star</option>
                      <option value="2">2 Stars</option>
                      <option value="3">3 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="5">5 Stars</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="status" className="text-gray-300">
                      Status
                    </Label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="reservation_contact_number"
                    className="text-gray-300"
                  >
                    Reservation Contact Number
                  </Label>
                  <Input
                    id="reservation_contact_number"
                    name="reservation_contact_number"
                    value={formData.reservation_contact_number}
                    onChange={handleInputChange}
                    placeholder="e.g., +1234567890 or 0123456789"
                    className={`bg-gray-800 border-gray-700 text-white ${
                      fieldErrors.reservation_contact_number
                        ? "border-red-500"
                        : ""
                    }`}
                  />
                  {fieldErrors.reservation_contact_number && (
                    <p className="text-red-400 text-sm mt-1">
                      {fieldErrors.reservation_contact_number}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">
                  Address Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="street_address" className="text-gray-300">
                    Street Address *
                  </Label>
                  <Input
                    id="street_address"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="area" className="text-gray-300">
                      Area
                    </Label>
                    <Input
                      id="area"
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="landmark" className="text-gray-300">
                      Landmark
                    </Label>
                    <Input
                      id="landmark"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-gray-300">
                      City *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleCityChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-gray-300">
                      State *
                    </Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-gray-300">
                      Country *
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleCountryChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zip_code" className="text-gray-300">
                      ZIP Code
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="destination" className="text-gray-300">
                      Destination *
                    </Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude" className="text-gray-300">
                      Latitude
                    </Label>
                    <Input
                      id="latitude"
                      name="latitude"
                      type="number"
                      step="0.000001"
                      value={formData.latitude ?? ""}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude" className="text-gray-300">
                      Longitude
                    </Label>
                    <Input
                      id="longitude"
                      name="longitude"
                      type="number"
                      step="0.000001"
                      value={formData.longitude ?? ""}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-gray-300">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., +1234567890 or 1234567890"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.phone ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.phone && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="reservation_email"
                      className="text-gray-300"
                    >
                      Reservation Email *
                    </Label>
                    <Input
                      id="reservation_email"
                      name="reservation_email"
                      type="email"
                      value={formData.reservation_email}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., reservations@hotel.com"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.reservation_email ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.reservation_email && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.reservation_email}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="support_email" className="text-gray-300">
                    Support Email
                  </Label>
                  <Input
                    id="support_email"
                    name="support_email"
                    type="email"
                    value={formData.support_email}
                    onChange={handleInputChange}
                    placeholder="e.g., support@hotel.com"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="hotel_website" className="text-gray-300">
                    Website (Optional)
                  </Label>
                  <Input
                    id="hotel_website"
                    name="hotel_website"
                    type="url"
                    value={formData.hotel_website}
                    onChange={handleInputChange}
                    placeholder="e.g., https://www.hotel.com"
                    className={`bg-gray-800 border-gray-700 text-white ${
                      fieldErrors.hotel_website ? "border-red-500" : ""
                    }`}
                  />
                  {fieldErrors.hotel_website && (
                    <p className="text-red-400 text-sm mt-1">
                      {fieldErrors.hotel_website}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Owner / Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="contact_person_name"
                      className="text-gray-300"
                    >
                      Contact Person Name *
                    </Label>
                    <Input
                      id="contact_person_name"
                      name="contact_person_name"
                      value={formData.contact_person_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter contact person name"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.contact_person_name ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.contact_person_name && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.contact_person_name}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="designation" className="text-gray-300">
                      Designation *
                    </Label>
                    <Input
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      required
                      placeholder="Owner / Manager"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.designation ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.designation && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.designation}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email" className="text-gray-300">
                      Contact Email *
                    </Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      required
                      placeholder="management@hotel.com"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.contact_email ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.contact_email && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.contact_email}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="contact_phone" className="text-gray-300">
                      Contact Mobile / Landline *
                    </Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., +1234567890 or 0123456789"
                      className={`bg-gray-800 border-gray-700 text-white ${
                        fieldErrors.contact_phone ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.contact_phone && (
                      <p className="text-red-400 text-sm mt-1">
                        {fieldErrors.contact_phone}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="tax_details" className="text-gray-300">
                    Tax Details (PAN / GST / VAT) *
                  </Label>
                  <Textarea
                    id="tax_details"
                    name="tax_details"
                    value={formData.tax_details}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    placeholder="Enter tax registration numbers and related information"
                    className={`bg-gray-800 border-gray-700 text-white ${
                      fieldErrors.tax_details ? "border-red-500" : ""
                    }`}
                  />
                  {fieldErrors.tax_details && (
                    <p className="text-red-400 text-sm mt-1">
                      {fieldErrors.tax_details}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="bank_account_details"
                    className="text-gray-300"
                  >
                    Bank Account Details *
                  </Label>
                  <Textarea
                    id="bank_account_details"
                    name="bank_account_details"
                    value={formData.bank_account_details}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    placeholder="Include account name, number, bank, branch, IFSC / SWIFT etc."
                    className={`bg-gray-800 border-gray-700 text-white ${
                      fieldErrors.bank_account_details ? "border-red-500" : ""
                    }`}
                  />
                  {fieldErrors.bank_account_details && (
                    <p className="text-red-400 text-sm mt-1">
                      {fieldErrors.bank_account_details}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="hotel_description" className="text-gray-300">
                    Hotel Description
                  </Label>
                  <Textarea
                    id="hotel_description"
                    name="hotel_description"
                    value={formData.hotel_description}
                    onChange={handleInputChange}
                    rows={4}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="terms_conditions" className="text-gray-300">
                    Terms & Conditions
                  </Label>
                  <Textarea
                    id="terms_conditions"
                    name="terms_conditions"
                    value={formData.terms_conditions}
                    onChange={handleInputChange}
                    rows={3}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="timezone" className="text-gray-300">
                    Timezone
                  </Label>
                  <Input
                    id="timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">
                  Operational Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="checkin_time" className="text-gray-300">
                      Check-in Time
                    </Label>
                    <Input
                      id="checkin_time"
                      name="checkin_time"
                      type="time"
                      value={formData.checkin_time}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout_time" className="text-gray-300">
                      Check-out Time
                    </Label>
                    <Input
                      id="checkout_time"
                      name="checkout_time"
                      type="time"
                      value={formData.checkout_time}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min_stay" className="text-gray-300">
                      Min Stay (nights)
                    </Label>
                    <Input
                      id="min_stay"
                      name="min_stay"
                      type="number"
                      min="1"
                      value={formData.min_stay}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_stay" className="text-gray-300">
                      Max Stay (nights)
                    </Label>
                    <Input
                      id="max_stay"
                      name="max_stay"
                      type="number"
                      min="1"
                      value={formData.max_stay}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="max_advance_booking_days"
                      className="text-gray-300"
                    >
                      Max Advance Booking (days)
                    </Label>
                    <Input
                      id="max_advance_booking_days"
                      name="max_advance_booking_days"
                      type="number"
                      min="1"
                      value={formData.max_advance_booking_days}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Pricing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="markup_percentage"
                      className="text-gray-300"
                    >
                      Markup %
                    </Label>
                    <Input
                      id="markup_percentage"
                      name="markup_percentage"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.markup_percentage}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="service_charge_percentage"
                      className="text-gray-300"
                    >
                      Service Charge %
                    </Label>
                    <Input
                      id="service_charge_percentage"
                      name="service_charge_percentage"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.service_charge_percentage}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Policies</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">
                    Accepted Payment Methods
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {acceptedPaymentMethods.length === 0 && (
                      <span className="text-sm text-gray-500">
                        No payment methods added yet.
                      </span>
                    )}
                    {acceptedPaymentMethods.map((method) => (
                      <span
                        key={method}
                        className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-blue-900/30 border border-blue-700 text-blue-300"
                      >
                        {method}
                        <button
                          type="button"
                          className="ml-2 text-blue-300 hover:text-blue-100"
                          onClick={() => removePaymentMethod(method)}
                          aria-label={`Remove ${method}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <Input
                    value={paymentMethodInput}
                    onChange={(e) => setPaymentMethodInput(e.target.value)}
                    onKeyDown={handlePaymentKeyDown}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Type a payment method and press Enter"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Press Enter or comma to add. Choose from suggestions or type
                    your own methods.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {defaultPaymentMethods.map((option) => {
                      const isSelected =
                        acceptedPaymentMethods.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => addPaymentMethod(option)}
                          disabled={isSelected}
                          className={`px-3 py-1 text-sm rounded-md border ${
                            isSelected
                              ? "border-blue-700 text-blue-300 bg-blue-900/20 cursor-not-allowed"
                              : "border-gray-700 text-gray-300 hover:border-blue-600 hover:text-blue-300"
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="cancellation_policy_text"
                    className="text-gray-300"
                  >
                    Cancellation Policy Text
                  </Label>
                  <Textarea
                    id="cancellation_policy_text"
                    name="cancellation_policy_text"
                    value={formData.cancellation_policy_text}
                    onChange={handleInputChange}
                    rows={3}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Enter the cancellation policy description that will be shown to guests"
                  />
                </div>

                <div className="border border-gray-700 rounded-md p-4 space-y-4">
                  <h4 className="text-gray-300 font-medium">
                    Cancellation Fee Configuration
                  </h4>
                  <p className="text-sm text-gray-400">
                    Set the number of days before check-in and the percentage
                    deduction for cancellations.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="cancellation_days"
                        className="text-gray-300"
                      >
                        Days before check-in
                      </Label>
                      <Input
                        id="cancellation_days"
                        name="cancellation_days"
                        type="number"
                        min="1"
                        max="30"
                        value={formData.cancellation_days}
                        onChange={handleInputChange}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Cancellation fee applies if cancelled within this many
                        days before check-in
                      </p>
                    </div>
                    <div>
                      <Label
                        htmlFor="cancellation_percentage"
                        className="text-gray-300"
                      >
                        Percentage deduction (%)
                      </Label>
                      <Input
                        id="cancellation_percentage"
                        name="cancellation_percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.cancellation_percentage}
                        onChange={handleInputChange}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Percentage of total amount to be charged as cancellation
                        fee
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-700 rounded-md p-4 space-y-4 mt-6">
                  <h4 className="text-gray-300 font-medium">
                    No Show Policy Configuration
                  </h4>
                  <p className="text-sm text-gray-400">
                    Set the no show policy text and percentage deduction for no
                    shows.
                  </p>

                  <div>
                    <Label
                      htmlFor="no_show_policy_text"
                      className="text-gray-300"
                    >
                      No Show Policy Text
                    </Label>
                    <Textarea
                      id="no_show_policy_text"
                      name="no_show_policy_text"
                      value={formData.no_show_policy_text}
                      onChange={handleInputChange}
                      rows={3}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Enter the no show policy description that will be shown to guests"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="no_show_percentage"
                      className="text-gray-300"
                    >
                      No Show Percentage Deduction (%)
                    </Label>
                    <Input
                      id="no_show_percentage"
                      name="no_show_percentage"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.no_show_percentage}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Percentage of total amount to be charged for no shows
                      (default: 100%)
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <Label htmlFor="child_policy" className="text-gray-300">
                    Child Policy
                  </Label>
                  <Textarea
                    id="child_policy"
                    name="child_policy"
                    value={formData.child_policy}
                    onChange={handleInputChange}
                    rows={3}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label htmlFor="child_rate" className="text-gray-300">
                      Child Rate
                    </Label>
                    <Input
                      id="child_rate"
                      name="child_rate"
                      type="number"
                      step="0.01"
                      value={formData.child_rate}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="extra_adult_rate" className="text-gray-300">
                      Extra Adult Rate
                    </Label>
                    <Input
                      id="extra_adult_rate"
                      name="extra_adult_rate"
                      type="number"
                      step="0.01"
                      value={formData.extra_adult_rate}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="extra_bed_rate" className="text-gray-300">
                      Extra Bed Rate
                    </Label>
                    <Input
                      id="extra_bed_rate"
                      name="extra_bed_rate"
                      type="number"
                      step="0.01"
                      value={formData.extra_bed_rate}
                      onChange={handleInputChange}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">
              Special Offers & Additional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="special_offer_name" className="text-gray-300">
                  Special Offer Name
                </Label>
                <Input
                  id="special_offer_name"
                  name="special_offer_name"
                  value={formData.special_offer_name}
                  onChange={handleInputChange}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="special_offer_type" className="text-gray-300">
                  Special Offer Type
                </Label>
                <select
                  id="special_offer_type"
                  name="special_offer_type"
                  value={formData.special_offer_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md"
                >
                  <option value="Percentage Discount">
                    Percentage Discount
                  </option>
                  <option value="Fixed Amount">Fixed Amount</option>
                  <option value="Free Night">Free Night</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="special_offer_details" className="text-gray-300">
                Special Offer Details
              </Label>
              <Textarea
                id="special_offer_details"
                name="special_offer_details"
                value={formData.special_offer_details}
                onChange={handleInputChange}
                rows={3}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="offer_valid_from" className="text-gray-300">
                  Offer Valid From
                </Label>
                <Input
                  id="offer_valid_from"
                  name="offer_valid_from"
                  type="date"
                  value={formData.offer_valid_from}
                  onChange={handleInputChange}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="offer_valid_to" className="text-gray-300">
                  Offer Valid To
                </Label>
                <Input
                  id="offer_valid_to"
                  name="offer_valid_to"
                  type="date"
                  value={formData.offer_valid_to}
                  onChange={handleInputChange}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="remarks" className="text-gray-300">
                Remarks
              </Label>
              <Textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                rows={3}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="special_conditions" className="text-gray-300">
                Special Conditions
              </Label>
              <Textarea
                id="special_conditions"
                name="special_conditions"
                value={formData.special_conditions}
                onChange={handleInputChange}
                rows={3}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Amenities Section */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Hotel Amenities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">
                Manage hotel amenities in the dedicated Amenities tab
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/hotels/${id}?tab=amenities`)}
                className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
              >
                Go to Amenities Tab
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/hotels/${id}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HotelEdit;
